import * as React from 'react';
import { useState } from 'react';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineDot from '@mui/lab/TimelineDot';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import DisplayHelper from '../helpers/display_helper';
import withDataLoaded from './with_data_loaded';
import PageSpinner from './shared/page_spinner';
import { customerHistoryLoader } from '../actions/data_loaders';
import CustomerHistoryHelper from './customer_history_helper';
import ShowMoreText from './forms/show_more_text';
import LocationHelper from '../helpers/location_helper';
import Actions from '../actions/actions';

const CustomerHistory = () => {
  const customerHistory = useSelector((state) => state?.customerHistory);
  const fridHistory = useSelector((state) => state?.fridHistory);
  const creditCheckHistory = useSelector((state) => state?.creditCheckHistory);
  const snapshotHistory = useSelector((state) => state?.snapshotHistory);
  const disbursementHistory = useSelector((state) => state?.disbursementHistory);
  const notificationHistory = useSelector((state) => state?.notificationHistory);
  const customerAuditTrail = useSelector((state) => state?.customerAuditTrail);
  const sponsorApiEventHistory = useSelector((state) => state?.sponsorApiEventHistory);
  const customerWebhookHistory = useSelector((state) => state?.customerWebhookHistory);
  const customerServiceLog = useSelector((state) => state?.customerServiceLog);

  let history = CustomerHistoryHelper.sanitize({
    customerHistory,
    fridHistory,
    creditCheckHistory,
    snapshotHistory,
    customerWebhookHistory,
    sponsorApiEventHistory,
    disbursementHistory,
    notificationHistory,
    customerAuditTrail,
    customerServiceLog,
  });

  const [checkBoxes, setCheckBoxes] = useState(
    LocationHelper.loadStateFromUrlQuery({
      Webhook: false,
      Ensemble: false,
      Customer: true,
      Financing: true,
      Step: true,
      Document: true,
      Credit: true,
      Stip: true,
      SMS: true,
      Invoice: false,
      Signer: false,
      Notification: false,
      AuditTrail: false,
      ChangeOrder: true,
      CustomerHold: true,
      ServiceLog: false,
    })
  );

  function getTimelineDot(historyItem) {
    return (
      <TimelineDot color={historyItem.color}>
        {historyItem?.icon ?? CustomerHistoryHelper.getIcon(historyItem.entity)}
      </TimelineDot>
    );
  }

  let entities = history.reduce((acc, h) => {
    acc[h.entity] = getTimelineDot({ ...h, color: 'primary' });
    return acc;
  }, {});

  function renderHistoryTimeline() {
    return (
      <div style={{ width: '100%' }}>
        <h2 style={{ textAlign: 'center' }}>Customer Id [{customerHistory[0]?.id}] History</h2>
        <Timeline position="right">
          {history
            .filter((historyItem) => {
              if (typeof checkBoxes[historyItem.entity] === 'boolean' && !checkBoxes[historyItem.entity]) {
                return false;
              }
              return !(typeof checkBoxes[historyItem.entity] === 'string' && checkBoxes[historyItem.entity] !== 'true');
            })
            .map((historyItem, i) => {
              return (
                <TimelineItem key={i} className={`qa-${historyItem.entity}-${i}`}>
                  <TimelineOppositeContent
                    sx={{ m: 'auto 0' }}
                    align={'right'}
                    variant="body1"
                    color="text.secondary"
                    className={`qa-${historyItem.entity}-${i}-time qa-history-${i}-time`}
                    data-historytime={i}
                  >
                    {DisplayHelper.formatDateV2Seconds(historyItem.time)}
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineConnector />
                    {getTimelineDot(historyItem)}
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Typography
                      variant={'h6'}
                      id={`qa-${historyItem.entity}-${historyItem.id}-contents qa-${i}-contents`}
                    >
                      <div data-historycontents={i}>
                        {historyItem.text.length > 64 ? (
                          <ShowMoreText text={historyItem.text} summary={historyItem.text.substr(0, 64)} />
                        ) : (
                          historyItem.text
                        )}
                      </div>
                    </Typography>
                    <Typography
                      // variant="h6"
                      component="span"
                      className={`qa-${historyItem.entity}-title qa-${i}-title`}
                      data-historytitle={i}
                    >
                      {getRenderIdName(historyItem.entity, historyItem.id)}
                    </Typography>

                    {historyItem.notes && (
                      <Typography variant={'span'} className={`qa-history-note`}>
                        <div style={{ display: 'flex' }}>
                          Note: <div data-historynote={i} style={{ marginLeft: '5px' }}>{`${historyItem.notes}`}</div>
                        </div>
                      </Typography>
                    )}

                    <Typography variant={'caption'} className={`qa-${historyItem.entity}-user qa-${i}-user`}>
                      <div data-historyuser={i}>{historyItem.user ? historyItem.user.username : ''}</div>
                    </Typography>
                    <Typography
                      variant={'caption'}
                      className={`qa-${historyItem.entity}-fallbackuser qa-${i}-fallbackuser`}
                    >
                      <div data-historyfallbackuser={i}>
                        {historyItem.fallbackUser ? historyItem.fallbackUser.username : ''}
                      </div>
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
        </Timeline>
      </div>
    );
  }

  function getRenderIdName(entity, id) {
    const names = {
      Document: 'Financing Request Step Doc',
      Financing: 'Financing Request',
      SMS: 'SMS Message',
      Stip: 'Stip Req',
      Credit: 'Credit Check',
      Customer: 'Quoting Customer',
      Step: 'Financing Request Step',
      ChangeOrder: 'Change Order Financing Request Old',
    };

    if (entity === 'SMS') {
      return `${names[entity]} [${id}]`;
    }

    if (id) {
      return `${names[entity] || entity} Id [${id}]`;
    }

    return names[entity] || entity;
  }

  function renderTimelineFilterToolbar() {
    return (
      <div
        style={{
          minWidth: '60%',
          display: 'flex',
          justifyContent: 'space-around',
          margin: '5px auto',
          // flexDirection: 'column',
        }}
      >
        <div key={-1} style={{ textAlign: 'center', alignContent: 'center' }}>
          <h3 key={-1} style={{ textAlign: 'center', alignContent: 'center', width: '75px', paddingTop: '40%' }}>
            Filters
          </h3>
        </div>

        {Object.keys(entities).map((entityType, i) => {
          let isActive =
            (typeof checkBoxes[entityType] === 'boolean' && checkBoxes[entityType]) ||
            (typeof checkBoxes[entityType] === 'string' && checkBoxes[entityType] === 'true');
          return (
            <div
              key={i}
              style={{ display: 'flex', flexDirection: 'column', textAlign: 'center', alignContent: 'center' }}
            >
              <div key={i} style={{ textAlign: 'center', alignContent: 'center', paddingBottom: '20px' }}>
                {entityType}
              </div>
              <label htmlFor={entityType} style={{ margin: 'auto' }}>
                <div
                  id={`qa-filter-${entityType}`}
                  className={`qa-filter-${entityType}`}
                  data-historyfilter={entityType}
                  style={{
                    width: '32px',
                    height: '32px',
                    color: isActive ? 'white' : 'black',
                    backgroundColor: isActive ? '#377bde' : '#bdbdbd',
                    borderRadius: '15px',
                    cursor: 'pointer',
                    boxShadow: !isActive ? '0px 0px 10px rgba(0, 0, 0, 0.5)' : 'inset 0px 0px 12px rgba(0, 0, 0, 0.7)',
                  }}
                >
                  <div style={{ paddingTop: '5px' }}>{CustomerHistoryHelper.getIcon(entityType)}</div>
                </div>
              </label>
              <input
                style={{ display: 'none' }}
                type="checkbox"
                id={entityType}
                name={entityType}
                onClick={() => {
                  Actions.setRoute(
                    `${window.location.pathname}?${LocationHelper.stateAsQuery(
                      { ...checkBoxes, [entityType]: !isActive },
                      null,
                      null,
                      '',
                      ''
                    )}`
                  );

                  setCheckBoxes({ ...checkBoxes, [entityType]: !isActive });
                }}
                defaultChecked={isActive}
              />
            </div>
          );
        })}
        <div key={-2} style={{ textAlign: 'center', alignContent: 'center' }}>
          <div key={-2} style={{ textAlign: 'center', alignContent: 'center', width: '75px', paddingTop: '50%' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {renderTimelineFilterToolbar()}
      <hr />
      {renderHistoryTimeline()}
    </div>
  );
};

const ConnectedCustomerHistoryWithFormWithData = withDataLoaded({
  WithData: CustomerHistory,
  WithoutData: PageSpinner,
  data: [customerHistoryLoader],
  emptyIsStillData: true,
});

export default ConnectedCustomerHistoryWithFormWithData;
